import { formatDate } from '@angular/common'
import { Injectable } from '@angular/core'
import { BannerVariant } from '../../shared/components/banner/banner.component'
import { AccountService } from './account.service'

// The two contracted-capacity thresholds that surface a console banner (per ARP-7551 / PRD).
export type CapacityThreshold = 100 | 106

export interface CapacityBannerState {
  variant: BannerVariant
  threshold: CapacityThreshold
  // Null when the contract has no capacity to measure against; only the 100% warning needs it.
  usedPercent: number | null
  // Bold lead-in + follow-on detail, matching the banner's "signal bar" copy style.
  lead: string
  detail: string
}

/**
 * Decides whether the account-capacity banner shows and what it says: warning at 100%, critical above 105%
 * of contracted billable resources, one banner updated in place. Dismissable per calendar day and re-shown
 * on a new threshold crossing (dismissal is keyed by account + day + threshold).
 */
@Injectable({ providedIn: 'root' })
export class CapacityBannerService {
  // In-memory mirror of dismissed keys so we don't hit localStorage on every change
  // detection pass; seeded lazily from localStorage.
  private dismissed: Set<string> | null = null

  constructor(private accountService: AccountService) {}

  getState(): CapacityBannerState | null {
    // Per the PRD, the capacity banner is a Fixed-mode (enforcement) feature only. Flex-mode
    // accounts get billing emails, not a console banner; `none` is neither metered nor enforced.
    // The mode, the percentage and the suspension flag all come from the entitlement as the
    // server derives them, so the UI never re-computes the thresholds. The banner is part of
    // Billing Notifications, so it follows that capability too.
    if (!this.accountService.fixedBillingEnabled || !this.accountService.billingNotificationsEnabled) {
      return null
    }

    // Critical (above 105%) follows the server's suspension flag, not the percentage, so it matches
    // enforcement and the Account Settings notice. Warning is the 100–105% grace band with backups still running.
    const usedPercent = this.accountService.usagePercent
    const threshold: CapacityThreshold | null =
      this.accountService.backupsSuspended === true ? 106 : usedPercent != null && usedPercent >= 100 ? 100 : null
    if (threshold == null || this.isDismissed(threshold)) {
      return null
    }

    return {
      threshold,
      usedPercent,
      variant: threshold === 106 ? 'critical' : 'warning',
      ...this.copyFor(threshold),
    }
  }

  dismiss(threshold: CapacityThreshold): void {
    const key = this.dismissKey(threshold)
    if (!key) return
    this.loadDismissed().add(key)
    try {
      localStorage.setItem(key, '1')
    } catch {
      // localStorage can throw in private mode; the in-memory set still hides it this session.
    }
  }

  // Verbatim PRD banner copy (ARP-7304 Billing Notifications → Banner Behavior).
  // The first sentence is the bold lead; the remainder is the detail.
  private copyFor(threshold: CapacityThreshold): { lead: string; detail: string } {
    return threshold === 106
      ? {
          lead: 'Backups suspended.',
          detail:
            'The number of resources has exceeded the contracted capacity. Reduce in-scope resources or contact your account team to restore backups.',
        }
      : {
          lead: "You've exceeded contracted capacity.",
          detail: 'Backups remain active through 105%. Reduce usage or contact your account team to avoid suspension.',
        }
  }

  private isDismissed(threshold: CapacityThreshold): boolean {
    const key = this.dismissKey(threshold)
    return !!key && this.loadDismissed().has(key)
  }

  // Keyed by account + calendar day + threshold: a dismissal only lasts the day,
  // and escalating to a new threshold produces a new key so the banner re-shows.
  private dismissKey(threshold: CapacityThreshold): string | null {
    const accountId = this.accountService.accountId
    if (!accountId) return null
    return `arpio.capacityBanner.${accountId}.${this.today()}.${threshold}`
  }

  // The user's local calendar day, so a dismissal lasts until their midnight.
  private today(): string {
    return formatDate(new Date(), 'yyyy-MM-dd', 'en-US')
  }

  // Seeds today's dismissals and drops earlier days' keys, so localStorage doesn't grow forever.
  private loadDismissed(): Set<string> {
    if (this.dismissed) return this.dismissed
    const set = new Set<string>()
    try {
      const keys: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith('arpio.capacityBanner.')) keys.push(key)
      }
      // Collected first: removing while iterating by index would skip entries.
      const todayMarker = `.${this.today()}.`
      for (const key of keys) {
        if (key.includes(todayMarker)) set.add(key)
        else localStorage.removeItem(key)
      }
    } catch {
      // ignore inaccessible localStorage
    }
    this.dismissed = set
    return set
  }
}
